import { useEffect, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';

const PROGRESS_TO_90_DURATION_MS = 400;
const PROGRESS_TO_100_DURATION_MS = 150;
/** Extra time to keep overlay visible after 100% so next step can render */
const POST_SUCCESS_DISPLAY_MS = 300;

interface AuthLoadingOverlayProps {
    /** Status text (e.g. "Signing in...", "Creating account...") */
    statusText: string;
    /** When true, progress animates from 90% to 100% */
    isSuccess: boolean;
    /** Called when progress reaches 100% after success */
    onComplete?: () => void;
    /** Use dark theme (e.g. for HDE) */
    dark?: boolean;
    /** Optional custom class for status text (e.g. HDE design spec) */
    statusTextClassName?: string;
}

/**
 * Overlay shown during login/signup submission.
 * Progress animates to 90% in 400ms, then to 100% only when auth succeeds.
 *
 * The progress bar is the only thing that animation drives, and it is drawn in
 * the dark theme alone. A light overlay therefore has nothing to animate
 * towards — it is a transparent layer that stops the accepted form being
 * resubmitted — so it hands over the moment the server confirms instead of
 * holding the reader on a form that no longer does anything. Whatever renders
 * next owns the waiting state from there.
 */
export function AuthLoadingOverlay({
    statusText,
    isSuccess,
    onComplete,
    dark = false,
    statusTextClassName,
}: AuthLoadingOverlayProps) {
    const [progress, setProgress] = useState(0);
    const startTimeRef = useRef<number>(Date.now());
    const successTimeRef = useRef<number | null>(null);
    const postSuccessTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    useEffect(() => {
        if (isSuccess && successTimeRef.current === null) {
            successTimeRef.current = Date.now();
        }
    }, [isSuccess]);

    useEffect(() => {
        if (!dark) {
            if (isSuccess) {
                onCompleteRef.current?.();
            }
            return;
        }

        startTimeRef.current = Date.now();
        if (!isSuccess) {
            successTimeRef.current = null;
        } else if (successTimeRef.current === null) {
            successTimeRef.current = Date.now();
        }
        setProgress(0);

        const animate = () => {
            const elapsed = Date.now() - startTimeRef.current;
            const successTime = successTimeRef.current;

            let target: number;
            if (successTime === null) {
                target = Math.min(
                    90,
                    (elapsed / PROGRESS_TO_90_DURATION_MS) * 90,
                );
            } else {
                const phase2Elapsed = Date.now() - successTime;
                target =
                    90 +
                    Math.min(1, phase2Elapsed / PROGRESS_TO_100_DURATION_MS) *
                        10;
            }

            const next = Math.min(100, target);
            setProgress(next);

            if (next >= 100 && successTime !== null) {
                postSuccessTimeoutRef.current = setTimeout(() => {
                    onCompleteRef.current?.();
                }, POST_SUCCESS_DISPLAY_MS);
                return;
            }

            requestAnimationFrame(animate);
        };

        const rafId = requestAnimationFrame(animate);

        return () => {
            cancelAnimationFrame(rafId);
            if (postSuccessTimeoutRef.current) {
                clearTimeout(postSuccessTimeoutRef.current);
            }
        };
    }, [isSuccess, dark]);

    return (
        <div
            className={cn(
                'absolute inset-0 z-50 flex flex-col items-center justify-center gap-6',
                dark ? 'bg-[#27282A]' : 'bg-transparent',
            )}
        >
            {statusText ? (
                <p
                    className={cn(
                        'font-sf-pro text-center text-[1.75rem] leading-[110%] font-normal tracking-[-0.0175rem] md:text-[2.5rem] md:tracking-[-0.03rem]',
                        dark ? 'text-white' : 'text-[#141414]',
                        statusTextClassName,
                    )}
                >
                    {statusText}
                </p>
            ) : null}
            {dark && (
                <div className="w-[12.875rem] shrink-0 md:w-[23.125rem]">
                    <div className="h-2.25 w-full overflow-hidden rounded-[62.4375rem] bg-white/20">
                        <div
                            className="h-full bg-white transition-[width] duration-75 ease-linear"
                            style={{ width: `${progress}%` }}
                            role="progressbar"
                            aria-valuenow={progress}
                            aria-valuemin={0}
                            aria-valuemax={100}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
